import type { StoreLiveStatus } from '../../../types/api.types';

function statusLabel(status: StoreLiveStatus): string {
    if (status === 'Active') return 'Live';
    if (status === 'Trial') return 'Trial';
    return 'Inactive';
}

function statusColor(status: StoreLiveStatus): string {
    if (status === 'Active') return 'text-[#15803d]';
    if (status === 'Trial') return 'text-[#92400e]';
    return 'text-[#64748b]';
}

interface ConfirmStatusDialogProps {
    isOpen: boolean;
    storeName: string;
    currentStatus: StoreLiveStatus;
    newStatus: StoreLiveStatus;
    onConfirm: () => void;
    onCancel: () => void;
}

export default function ConfirmStatusDialog({
    isOpen,
    storeName,
    currentStatus,
    newStatus,
    onConfirm,
    onCancel,
}: ConfirmStatusDialogProps) {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onCancel}
            />

            {/* Dialog */}
            <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 p-6">
                {/* Icon */}
                <div className="w-12 h-12 rounded-full bg-[#fef3c7] flex items-center justify-center mx-auto mb-4">
                    <svg
                        className="w-6 h-6 text-[#f59e0b]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                        />
                    </svg>
                </div>

                {/* Content */}
                <h3 className="text-lg font-semibold text-[#1e293b] text-center mb-2">
                    Change store status?
                </h3>
                <p className="text-sm text-[#64748b] text-center mb-6">
                    You&apos;re about to change{' '}
                    <span className="font-semibold text-[#1e293b]">{storeName}</span> from{' '}
                    <span className={`font-semibold ${statusColor(currentStatus)}`}>
                        {statusLabel(currentStatus)}
                    </span>{' '}
                    to{' '}
                    <span className={`font-semibold ${statusColor(newStatus)}`}>
                        {statusLabel(newStatus)}
                    </span>
                    . This takes effect immediately for customers and staff.
                </p>

                {/* Actions */}
                <div className="flex gap-3">
                    <button
                        onClick={onCancel}
                        className="flex-1 px-4 py-2.5 rounded-lg border border-[#e2e8f0] text-[#64748b] hover:bg-[#f8fafc] transition-colors text-sm font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="flex-1 px-4 py-2.5 rounded-lg bg-[#6e64f0] text-white hover:bg-[#5b53d4] transition-colors text-sm font-medium"
                    >
                        Change status
                    </button>
                </div>
            </div>
        </div>
    );
}
